// src/pages/CongNoNhapPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, query, orderBy as fbOrderBy } from 'firebase/firestore';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  TablePagination,
  Chip,
  Grid,
  Autocomplete,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';

// Helper Functions
const formatDateTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp.seconds * 1000).toLocaleString('vi-VN');
};
const formatCurrency = (number) => {
  if (typeof number !== 'number' || isNaN(number)) return '0 ₫';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(number);
};

function CongNoNhapPage() {
  const [imports, setImports] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { currentUser } = useAuth();

  // State for filters
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  
  const canViewAll = currentUser?.roleNames?.some(name => name.toLowerCase() === 'admin') || currentUser?.permissions?.full_access || currentUser?.permissions?.viewAllData;
  const [viewMode, setViewMode] = useState(canViewAll ? 'all' : 'own');

  useEffect(() => {
    const unsubSuppliers = onSnapshot(collection(db, 'suppliers'), (snapshot) => {
      setSuppliers(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    const q = query(collection(db, 'imports'), fbOrderBy('importDate', 'desc'));
    const unsubImports = onSnapshot(q, (snapshot) => {
      setImports(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });

    return () => {
      unsubSuppliers();
      unsubImports();
    };
  }, []);

  const activeSuppliers = useMemo(() => {
    const supplierIdsInImports = new Set(imports.map((imp) => imp.supplierId));
    return suppliers.filter((sup) => supplierIdsInImports.has(sup.id));
  }, [imports, suppliers]);

  const { detailedItems, totalPayable } = useMemo(() => {
    const start = startDate ? startOfDay(startDate) : null;
    const end = endDate ? endOfDay(endDate) : null;

    const baseFilteredImports = imports.filter(slip => {
        if (canViewAll && viewMode === 'all') {
            return true;
        }
        return slip.createdBy === currentUser.email; 
    });

    const filteredSlips = baseFilteredImports.filter((slip) => {
      const importDate = slip.importDate?.toDate();
      if (!importDate) return false;
      const dateMatch = start && end ? importDate >= start && importDate <= end : true;
      const supplierMatch = selectedSupplier ? slip.supplierId === selectedSupplier.id : true;
      return dateMatch && supplierMatch;
    });

    const items = filteredSlips.flatMap((slip) =>
      (slip.items || []).map((item) => ({
        ...item,
        importDate: slip.importDate,
        poNumber: slip.poNumber,
        invoiceNumber: slip.invoiceNumber,
        supplierName: slip.supplierName,
        createdBy: slip.createdBy,
        approvedBy: slip.approvedBy || 'Chưa duyệt',
        slipId: slip.id,
      })),
    );

    const total = items.reduce((sum, item) => sum + item.quantity * item.importPrice, 0);

    return { detailedItems: items, totalPayable: total };
  }, [imports, startDate, endDate, selectedSupplier, currentUser, viewMode, canViewAll]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportExcel = () => {
    if (detailedItems.length === 0) {
      alert('Không có dữ liệu để xuất.');
      return;
    }
    const dataToExport = detailedItems.map((item) => ({
      'Ngày nhập': formatDateTime(item.importDate),
      'Mã PO': item.poNumber,
      'Số Hóa đơn': item.invoiceNumber,
      'Nhà cung cấp': item.supplierName,
      'Tên sản phẩm': item.productName,
      'Số lượng': item.quantity,
      'Giá nhập': item.importPrice,
      'Thành tiền': item.quantity * item.importPrice,
      'Người tạo': item.createdBy,
      'Người duyệt': item.approvedBy,
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CongNoNhap');
    XLSX.writeFile(
      wb,
      `CongNoPhaiTra_${new Date().toLocaleDateString('vi-VN').replace(/\//g, '-')}.xlsx`,
    );
  };

  if (loading) return <Typography>Đang tải dữ liệu...</Typography>;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Báo Cáo Công Nợ Phải Trả (Nhập Hàng)
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <DatePicker
              label="Từ ngày"
              value={startDate}
              onChange={setStartDate}
              renderInput={(params) => <TextField {...params} fullWidth size="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <DatePicker
              label="Đến ngày"
              value={endDate}
              onChange={setEndDate}
              renderInput={(params) => <TextField {...params} fullWidth size="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={6}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={{ width: '100%', height: '100%' }}
            >
              Xuất Excel
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={activeSuppliers}
              getOptionLabel={(option) => option.name}
              value={selectedSupplier}
              onChange={(event, newValue) => setSelectedSupplier(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lọc theo Nhà Cung Cấp (chỉ hiện NCC có đơn hàng)"
                  size="small"
                />
              )}
            />
          </Grid>
          {canViewAll && (
            <Grid item xs={12}>
                <ToggleButtonGroup
                    color="primary"
                    value={viewMode}
                    exclusive
                    onChange={(e, newMode) => { if (newMode !== null) setViewMode(newMode); }}
                    aria-label="Chế độ xem"
                    size="small"
                >
                    <ToggleButton value="own">Xem của tôi</ToggleButton>
                    <ToggleButton value="all">Xem tất cả</ToggleButton>
                </ToggleButtonGroup>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ngày nhập</TableCell>
                <TableCell>Mã PO</TableCell>
                <TableCell>Số Hóa đơn</TableCell>
                <TableCell>Nhà cung cấp</TableCell>
                <TableCell>Tên sản phẩm</TableCell>
                <TableCell align="right">Số lượng</TableCell>
                <TableCell align="right">Giá nhập</TableCell>
                <TableCell align="right">Thành tiền</TableCell>
                <TableCell>Người tạo</TableCell>
                <TableCell>Người duyệt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detailedItems
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => (
                  <TableRow key={`${item.slipId}-${index}`} hover>
                    <TableCell>{formatDateTime(item.importDate)}</TableCell>
                    <TableCell>{item.poNumber}</TableCell>
                    <TableCell>{item.invoiceNumber}</TableCell>
                    <TableCell>{item.supplierName}</TableCell>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(item.importPrice)}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.quantity * item.importPrice)}
                    </TableCell>
                    <TableCell>{item.createdBy}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.approvedBy === 'Chưa duyệt' ? 'Chưa duyệt' : 'Đã duyệt'}
                        color={item.approvedBy === 'Chưa duyệt' ? 'default' : 'success'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={detailedItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Số dòng mỗi trang:"
        />
      </Paper>

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" align="right">
          Tổng công nợ phải trả (theo bộ lọc):{' '}
          <Typography
            component="span"
            variant="h6"
            color="error.main"
            sx={{ fontWeight: 'bold' }}
          >
            {formatCurrency(totalPayable)}
          </Typography>
        </Typography>
      </Paper>
    </Box>
  );
}

export default CongNoNhapPage;
